import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Car, Shield, Clock, Star, ArrowRight, Users, Fuel, Settings2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { t } = useLanguage();
  const [pickupDate, setPickupDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [featuredCars, setFeaturedCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        // First try to seed data
        await axios.post(`${API}/seed`).catch(() => {});
        const response = await axios.get(`${API}/cars`);
        setFeaturedCars(response.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const features = [
    { icon: Shield, title: t('bestPrices'), desc: 'Competitive rates guaranteed' },
    { icon: Clock, title: t('newCars'), desc: 'Always here when you need us' },
    { icon: Star, title: t('freeCancel'), desc: 'Change plans? No problem' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/50 to-background"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl pt-24">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-foreground mb-2 animate-slide-up opacity-0 stagger-1">
              {t('heroTitle')}
            </h1>
            <h2 className="font-serif text-4xl md:text-6xl font-bold text-gold mb-6 animate-slide-up opacity-0 stagger-2">
              {t('heroSubtitle')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl animate-slide-up opacity-0 stagger-3">
              {t('heroDescription')}
            </p>

            {/* Search Box */}
            <div className="glass rounded-2xl p-6 md:p-8 animate-slide-up opacity-0 stagger-4" data-testid="search-box">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Pickup Date */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('pickupDate')}
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="pickup-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {pickupDate ? format(pickupDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Return Date */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('returnDate')}
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="return-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {returnDate ? format(returnDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={returnDate}
                        onSelect={setReturnDate}
                        disabled={(date) => date < (pickupDate || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <Link to="/fleet" className="w-full">
                    <Button 
                      className="w-full h-12 bg-accent hover:bg-accent/90 text-white"
                      data-testid="search-btn"
                    >
                      {t('search')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-secondary/30" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-4">
            {t('whyChooseUs')}
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Experience premium car rental service with unmatched quality and support
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 bg-card border border-border/50 rounded-2xl hover:shadow-lg transition-all duration-300 card-hover"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <feature.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Fleet Section */}
      <section className="py-20" data-testid="fleet-preview-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-2">
                {t('ourFleet')}
              </h2>
              <p className="text-muted-foreground max-w-xl">
                {t('fleetDescription')}
              </p>
            </div>
            <Link to="/fleet">
              <Button variant="outline" className="mt-4 md:mt-0" data-testid="view-all-fleet-btn">
                {t('viewFleet')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-muted"></div>
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.map((car) => (
                <Link
                  key={car.id}
                  to={`/car/${car.id}`}
                  className="group bg-card border border-border/50 rounded-xl overflow-hidden card-hover"
                  data-testid={`car-card-${car.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={car.image_url}
                      alt={car.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 text-xs font-medium rounded-full capitalize">
                        {car.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-serif text-lg font-semibold">{car.name}</h3>
                        <p className="text-sm text-muted-foreground">{car.brand} • {car.year || 2024}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-gold">€{car.price_per_day}</span>
                        <span className="text-sm text-muted-foreground">{t('perDay')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {car.seats}
                      </span>
                      <span className="flex items-center gap-1">
                        <Settings2 className="w-4 h-4" />
                        {car.transmission === 'automatic' ? t('automatic') : t('manual')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Fuel className="w-4 h-4" />
                        {car.fuel_type}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6">
            Ready to Hit the Road?
          </h2>
          <p className="text-white/70 mb-10 max-w-xl mx-auto text-lg">
            Book your perfect car today and explore Greece like never before
          </p>
          <Link to="/fleet">
            <Button 
              size="lg" 
              className="bg-gold hover:bg-gold/90 text-primary px-10 py-6 text-lg font-semibold"
              data-testid="cta-book-btn"
            >
              {t('bookNow')}
              <Car className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
